import { mathUtils } from "@/lib/utils.js";
import { formatNumber, removeCommasAndSpaces } from "@/lib/formatting-utils.js";

/**
 * Gauge component for displaying tag parameter values with alarm thresholds
 * @param {Object} param - Parameter data with currentValue, alarmSetPoints, etc.
 * @param {number} index - Index for unique mask IDs
 */
const Gauge = ({ param, index }) => {
  // Validate alarm set points
  const alarmSetPoints = param.alarmSetPoints || {};
  const lolo = parseFloat(alarmSetPoints.lolo) || 0;
  const lo = parseFloat(alarmSetPoints.lo) || 0;
  const hi = parseFloat(alarmSetPoints.hi) || 0;
  const hihi = parseFloat(alarmSetPoints.hihi) || 0;

  // Handle different number formats (commas, spaces, etc.)
  let currentValueStr = removeCommasAndSpaces(param.currentValue);
  const currentValue = parseFloat(currentValueStr);
  const isValidCurrentValue = !isNaN(currentValue) && isFinite(currentValue);

  // Use API min/max values for scaling
  const minValue = parseFloat(param.apiData?.minValue) || 0;
  const maxValue = parseFloat(param.apiData?.maxValue) || 100;
  const range = maxValue - minValue || 1;

  // Sort alarm set points to ensure proper order: LOLO < LO < HI < HIHI
  const sortedAlarms = [lolo, lo, hi, hihi].sort((a, b) => a - b);
  const sortedLolo = sortedAlarms[0];
  const sortedLo = sortedAlarms[1];
  const sortedHi = sortedAlarms[2];
  const sortedHihi = sortedAlarms[3];

  let loloAngle = ((sortedLolo - minValue) / range) * 180;
  let loAngle = ((sortedLo - minValue) / range) * 180;
  let hiAngle = ((sortedHi - minValue) / range) * 180;
  let hihiAngle = ((sortedHihi - minValue) / range) * 180;

  const clampAngle = (angle) => {
    if (Math.abs(angle - 90) < 1) {
      angle = angle > 90 ? 91 : 89;
    }
    if (angle < 2) angle = 2;
    if (angle > 178) angle = 178;
    return angle;
  };

  loloAngle = clampAngle(loloAngle);
  loAngle = clampAngle(loAngle);
  hiAngle = clampAngle(hiAngle);
  hihiAngle = clampAngle(hihiAngle);

  const createAngularMask = (startAngle, endAngle, maskId) => {
    const centerX = 50,
      centerY = 50;
    const outerRadius = 60;
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    const startX = centerX + outerRadius * Math.cos(Math.PI - startRad);
    const startY = centerY - outerRadius * Math.sin(Math.PI - startRad);
    const endX = centerX + outerRadius * Math.cos(Math.PI - endRad);
    const endY = centerY - outerRadius * Math.sin(Math.PI - endRad);
    const largeArcFlag = endAngle - startAngle > 90 ? 1 : 0;
    const sectorPath = `M ${centerX} ${centerY} L ${startX} ${startY} A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${endX} ${endY} Z`;

    return (
      <mask id={maskId} key={maskId}>
        <rect width='100' height='60' fill='black' />
        <path d={sectorPath} fill='white' />
      </mask>
    );
  };

  const arcPath = "M 10 50 A 40 40 0 0 1 90 50";

  // Calculate current value position with validation
  const isValidValue = isValidCurrentValue;
  const isValidMaxValue = maxValue > 0 && isFinite(maxValue);

  let dotX,
    dotY,
    shouldShowDot = false;
  let percentage = 0;

  if (isValidValue && isValidMaxValue) {
    percentage = mathUtils.min(
      1,
      mathUtils.max(0, (currentValue - minValue) / range)
    );
    // Proper arc calculation: 0% = left (180°), 50% = top (90°), 100% = right (0°)
    const angle = Math.PI - percentage * Math.PI; // 0% = π (180°), 100% = 0 (0°)
    dotX = 50 + 40 * Math.cos(angle);
    dotY = 50 - 40 * Math.sin(angle);

    // Ensure coordinates are within the SVG bounds
    dotX = Math.max(10, Math.min(90, dotX));
    dotY = Math.max(10, Math.min(90, dotY));

    shouldShowDot = true;
  } else {
    // Fallback position for invalid data
    dotX = 50;
    dotY = 50;
    shouldShowDot = false;
  }

  return (
    <div className='relative mx-auto w-32 h-20 mb-4'>
      <svg className='w-full h-full' viewBox='0 0 100 60'>
        <defs>
          {/* Masks for different color zones - using sorted angles */}
          {[
            createAngularMask(0, loloAngle, `mask-red1-${index}`),
            createAngularMask(loloAngle, loAngle, `mask-yellow1-${index}`),
            createAngularMask(loAngle, hiAngle, `mask-green-${index}`),
            createAngularMask(hiAngle, hihiAngle, `mask-yellow2-${index}`),
            createAngularMask(hihiAngle, 180, `mask-red2-${index}`),
          ]}
        </defs>

        {/* Single half circle with different colored sections using masks */}
        {[
          <path
            key='red1'
            d={arcPath}
            stroke='#dc2626'
            strokeWidth='8'
            fill='none'
            opacity='0.8'
            mask={`url(#mask-red1-${index})`}
          />,
          <path
            key='yellow1'
            d={arcPath}
            stroke='#fbbf24'
            strokeWidth='8'
            fill='none'
            opacity='0.8'
            mask={`url(#mask-yellow1-${index})`}
          />,
          <path
            key='green'
            d={arcPath}
            stroke='#10b981'
            strokeWidth='8'
            fill='none'
            opacity='0.8'
            mask={`url(#mask-green-${index})`}
          />,
          <path
            key='yellow2'
            d={arcPath}
            stroke='#fbbf24'
            strokeWidth='8'
            fill='none'
            opacity='0.8'
            mask={`url(#mask-yellow2-${index})`}
          />,
          <path
            key='red2'
            d={arcPath}
            stroke='#dc2626'
            strokeWidth='8'
            fill='none'
            opacity='0.8'
            mask={`url(#mask-red2-${index})`}
          />,
        ]}

        {/* Current value dot indicator - only show for valid data */}
        {shouldShowDot && (
          <circle
            cx={dotX}
            cy={dotY}
            r='4'
            fill={currentValue > hihi ? "#ef4444" : "#1f2937"}
            stroke='#ffffff'
            strokeWidth='2'
          />
        )}
      </svg>
      <div className='absolute inset-0 flex items-end justify-center pb-1'>
        <div className='text-center'>
          <div
            className={`text-lg font-bold ${!shouldShowDot ? "text-red-600" : currentValue > hihi ? "text-red-600" : ""}`}>
            {isValidValue ? formatNumber(currentValue, 2) : "N/A"}
          </div>
          {!shouldShowDot && (
            <div className='text-xs text-red-500 mt-1'>Invalid Data</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Gauge;
